import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Pause, Play, RotateCcw } from "lucide-react";
import { useT } from "@/i18n";
import { PROMPTER_TEXT } from "@/components/story/content";
import VoiceRecorder from "@/components/story/VoiceRecorder";

export default function Teleprompter({ script, cover, onBack }) {
  const { lang } = useT();
  const tx = PROMPTER_TEXT[lang];
  const boxRef = useRef(null);
  const posRef = useRef(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(2);
  const [size, setSize] = useState(36);

  useEffect(() => {
    if (!playing) return undefined;
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const box = boxRef.current;
      posRef.current += ((now - last) / 1000) * speed * 14;
      last = now;
      const max = box.scrollHeight - box.clientHeight;
      if (posRef.current >= max) { posRef.current = max; setPlaying(false); }
      box.scrollTop = posRef.current;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, speed]);

  const sync = () => { posRef.current = boxRef.current.scrollTop; };
  const reset = () => { setPlaying(false); posRef.current = 0; boxRef.current.scrollTop = 0; };

  return (
    <div className="space-y-5" data-testid="story-prompter">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-2 font-bold text-white drop-shadow" data-testid="prompter-back-btn">
        <ArrowLeft className="w-4 h-4" /> {tx.back}
      </button>
      <div className="relative rounded-[2rem] overflow-hidden bg-slate-950 border-4 border-slate-800 shadow-2xl">
        {cover && <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" data-testid="prompter-cover-bg" />}
        <div className="absolute left-0 right-0 top-1/3 h-1 bg-yellow-300/80 z-10 pointer-events-none" />
        <div ref={boxRef} onScroll={() => { if (!playing) posRef.current = boxRef.current.scrollTop; }} onWheel={sync} onTouchMove={sync} className="relative h-[26rem] overflow-y-auto no-scrollbar px-6 sm:px-14" data-testid="prompter-text">
          <div className="h-[9rem]" />
          {cover && <img src={cover} alt="" className="mx-auto w-48 h-48 object-cover rounded-3xl border-4 border-white shadow-xl" data-testid="prompter-cover-img" />}
          <h3 className="font-fredoka font-bold text-yellow-300 text-center mt-6" style={{ fontSize: size * 1.1 }}>{script.title}</h3>
          {script.scenes.map((s, i) => (
            <p key={i} className="font-fredoka font-bold text-white text-center leading-snug mt-8" style={{ fontSize: size }}>{s.text}</p>
          ))}
          <div className="h-[20rem]" />
        </div>
      </div>
      <p className="text-center font-bold text-white drop-shadow">{tx.tip}</p>
      <div className="bg-white rounded-[2rem] shadow-lg p-5 flex flex-wrap items-center gap-5">
        <button type="button" onClick={() => setPlaying(!playing)} className="glossy-btn bg-teal-500 text-white font-fredoka font-bold px-6 py-3 rounded-full inline-flex items-center gap-2" data-testid="prompter-play-btn">
          {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />} {playing ? tx.pause : tx.play}
        </button>
        <button type="button" onClick={reset} className="inline-flex items-center gap-2 font-bold text-blue-900" data-testid="prompter-reset-btn"><RotateCcw className="w-4 h-4" /> {tx.reset}</button>
        <label className="flex items-center gap-2 font-bold text-blue-900">{tx.speed}
          <input type="range" min="1" max="6" value={speed} onChange={(e) => setSpeed(+e.target.value)} className="accent-teal-500" data-testid="prompter-speed" />
        </label>
        <label className="flex items-center gap-2 font-bold text-blue-900">{tx.size}
          <input type="range" min="24" max="56" value={size} onChange={(e) => setSize(+e.target.value)} className="accent-teal-500" data-testid="prompter-size" />
        </label>
      </div>
      <VoiceRecorder onStart={() => { reset(); setPlaying(true); }} onStop={() => setPlaying(false)} title={script.title} />
    </div>
  );
}
